import type { ReactNode } from 'react';
import { Breadcrumbs } from './Breadcrumbs';
import type { Crumb } from './Breadcrumbs';

/* ── Types ────────────────────────────────────────────────────────── */

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  /** Inline badge rendered after the title (e.g. <StatusBadge />) */
  badge?: ReactNode;
  /** Right-aligned action slot (e.g. a Button) */
  action?: ReactNode;
  /** Optional breadcrumbs rendered above the title */
  breadcrumbs?: Crumb[];
  className?: string;
}

/* ── Component ────────────────────────────────────────────────────── */

/**
 * PageHeader — standardized page title block.
 *
 *   <PageHeader
 *     title="Sales Intelligence"
 *     subtitle="AI-powered deal signals and insights"
 *     badge={<StatusBadge status="info" label="AI" size="xs" />}
 *     action={<Button variant="secondary" size="sm">Export</Button>}
 *     breadcrumbs={[
 *       { label: 'Dashboard', to: '/dashboard' },
 *       { label: 'Sales Intelligence' },
 *     ]}
 *   />
 */
export function PageHeader({
  title,
  subtitle,
  badge,
  action,
  breadcrumbs,
  className,
}: PageHeaderProps) {
  return (
    <div className={className}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs items={breadcrumbs} />
      )}

      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-ink">{title}</h1>
            {badge}
          </div>
          {subtitle && (
            <p className="mt-1 text-2xs text-ink-dim">{subtitle}</p>
          )}
        </div>

        {action && <div className="shrink-0">{action}</div>}
      </div>
    </div>
  );
}
